<template>
  <section id="courses" class="section-padding relative overflow-hidden transition-colors duration-500"
    :class="isDark ? 'galaxy-section-dark' : 'galaxy-section-light'"
  >
    <!-- Background effects -->
    <div class="absolute inset-0 pointer-events-none overflow-hidden">
      <div v-if="isDark" class="absolute inset-0 stars-bg opacity-30"></div>
      <div class="absolute top-1/4 left-0 w-72 h-72 rounded-full blur-3xl opacity-10"
        :class="isDark ? 'bg-nebula-blue' : 'bg-primary-200'"
      ></div>
      <div class="absolute bottom-1/4 right-0 w-72 h-72 rounded-full blur-3xl opacity-10"
        :class="isDark ? 'bg-nebula-pink' : 'bg-secondary-200'"
      ></div>
    </div>

    <div class="max-w-6xl mx-auto relative z-10">
      <div class="text-center mb-12">
        <h2 class="text-3xl md:text-4xl font-bold mb-4">
          <span class="galaxy-text-gradient">{{ t('courses.sectionTitle') }}</span>
        </h2>
        <p class="text-lg" :class="isDark ? 'text-galaxy-200' : 'text-gray-600'">
          {{ t('courses.subtitle') }}
        </p>
      </div>

      <div class="grid md:grid-cols-3 gap-8">
        <div
          v-for="level in levels"
          :key="level.key"
          class="group"
        >
          <div class="galaxy-card overflow-hidden h-full flex flex-col">
            <!-- Level header -->
            <div :class="level.bgClass" class="p-6 text-white text-center relative overflow-hidden">
              <div v-if="isDark" class="absolute inset-0 stars-bg opacity-40"></div>
              <div class="relative z-10">
                <div class="text-4xl mb-3">{{ level.icon }}</div>
                <h3 class="text-xl font-bold">{{ t(`courses.levels.${level.key}.name`) }}</h3>
                <p class="text-white/80 text-sm mt-1">{{ t(`courses.levels.${level.key}.ages`) }}</p>
              </div>
            </div>

            <div class="p-6 flex-1">
              <p class="text-center mb-4"
                :class="isDark ? 'text-galaxy-200' : 'text-gray-600'"
              >
                {{ t(`courses.levels.${level.key}.description`) }}
              </p>

              <!-- Expandable courses -->
              <q-expansion-item
                v-for="subject in subjects"
                :key="subject.key"
                :label="t(`courses.subjects.${subject.key}.title`)"
                :icon="subject.icon"
                :header-class="isDark ? 'text-nebula-cyan font-semibold' : 'text-primary-700 font-semibold'"
                dense
              >
                <q-card flat :class="isDark ? 'bg-transparent' : ''">
                  <q-card-section class="text-sm"
                    :class="isDark ? 'text-galaxy-200' : 'text-gray-600'"
                  >
                    {{ t(`courses.subjects.${subject.key}.description`) }}
                  </q-card-section>
                </q-card>
              </q-expansion-item>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { useI18n } from 'vue-i18n'

defineProps<{ isDark: boolean }>()

const { t } = useI18n()

const levels = [
  { key: 'little_explorers', icon: '🚀', bgClass: 'bg-gradient-to-br from-nebula-cyan/80 to-nebula-blue/80' },
  { key: 'young_innovators', icon: '💡', bgClass: 'bg-gradient-to-br from-nebula-purple/80 to-galaxy-600/80' },
  { key: 'teen_creators', icon: '🎯', bgClass: 'bg-gradient-to-br from-nebula-pink/80 to-secondary-500/80' }
]

const subjects = [
  { key: 'robotics', icon: 'smart_toy' },
  { key: 'programming', icon: 'code' },
  { key: 'ai', icon: 'psychology' },
  { key: 'animation', icon: 'movie' }
]
</script>
